import { useQuery } from "@tanstack/react-query";
import { fetchUserTools } from "@/services/api";
import { useAuthToken } from "@/contexts/OptimizedAuthContext";
import { ensureAuthenticated } from "@/services/api";
import { AGENT_CONFIG } from "@/lib/constants";
import { getToolDisplayName } from "@/lib/utils/formatting";

export interface ConnectedTool {
  id: string;
  name: string;
  logo: string;
  lastUsed: string;
  sessionCount: number;
  docsUrl: string;
}

export function useConnectedTools() {
  const getToken = useAuthToken();

  const { data: connectedTools = [], isLoading: isLoadingTools, refetch } = useQuery({
    queryKey: ["connectedTools"],
    queryFn: async () => {
      await ensureAuthenticated(getToken);
      // Fetch aggregated tool stats from the backend
      const userTools = await fetchUserTools();

      const tools: ConnectedTool[] = userTools.map((tool) => {
        const agentName = tool.agentName;

        // Map to display name and icon using AGENT_CONFIG
        const knownConfig = AGENT_CONFIG[agentName as keyof typeof AGENT_CONFIG];

        // Use shared utility for display name, fallback for icon/docs
        const logo = knownConfig?.icon || AGENT_CONFIG.default.icon;
        const docsUrl = knownConfig?.docsUrl || AGENT_CONFIG.default.docsUrl;

        return {
          id: agentName,
          name: getToolDisplayName(agentName),
          logo,
          lastUsed: tool.lastUsed,
          sessionCount: tool.sessionCount,
          docsUrl,
        };
      });

      // Tools are already sorted by the backend (sessionCount desc, lastUsed desc)
      return tools;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return { connectedTools, isLoadingTools, refetch };
}
